import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from '../../axios';
import config from '../../config';
import './Blog.css';

const BlogPostForm = () => {
  const { postId } = useParams();
  const isEditing = postId && postId !== 'new' && postId !== 'undefined';
  const history = useHistory();
  const backend_url = config.backend_url.replace(/\/$/, '');
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [currentImage, setCurrentImage] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Check admin status on load
  useEffect(() => {
    const checkAdmin = async () => {
      // First check session storage
      const isLoggedIn = sessionStorage.getItem('blog_admin_logged_in') === 'true';
      
      if (!isLoggedIn) {
        // Verify with server
        try {
          const response = await axios.get(
            `${backend_url}/blog/admin/check`,
            { withCredentials: true }
          );
          
          if (!response.data.is_admin) {
            // Redirect to login
            history.push('/adminblog');
          }
        } catch (err) {
          console.error('Error checking admin status:', err);
          // Redirect to login
          history.push('/adminblog');
        }
      }
    };
    
    checkAdmin();
  }, [backend_url, history]);
  
  // If editing, fetch the post data
  useEffect(() => {
    if (isEditing && postId && postId !== 'undefined') {
      const fetchPost = async () => {
        try {
          setLoading(true);
          const url = `${backend_url}/blog/posts/${postId}?admin=true`;
          
          const response = await axios.get(url, { withCredentials: true });
          
          if (response.data.success) {
            const post = response.data.post;
            setTitle(post.title);
            setContent(post.content);
            setAuthor(post.author);
            setIsPublished(post.is_published);
            
            if (post.image_filename) {
              setCurrentImage(post.image_filename);
              setImagePreview(`${backend_url}/static/blog_images/${post.image_filename}`);
            }
          } else {
            setError('Failed to fetch blog post');
          }
        } catch (err) {
          console.error('Error fetching blog post:', err);
          setError('Failed to fetch blog post. Please try again later.');
        } finally {
          setLoading(false);
        }
      };
      
      fetchPost();
    }
  }, [isEditing, postId, backend_url]);
  
  // Handle image file change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !content || !author) {
      setError('Titel, innehåll och författare är obligatoriska fält');
      return;
    }
    
    try {
      setSaving(true);
      setError(''); // Clear any existing errors
      
      // Debug information
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('author', author);
      formData.append('is_published', isPublished);
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      let response;
      let url;
      
      if (isEditing) {
        // Update existing post
        url = `${backend_url}/blog/posts/${postId}`;
        
        response = await axios.put(
          url,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            withCredentials: true
          }
        );
      } else {
        // Create new post
        url = `${backend_url}/blog/posts`;
        
        response = await axios.post(
          url,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            withCredentials: true
          }
        );
      }
      
      if (response.data.success) {
        history.push('/adminblog/posts');
      } else {
        setError(`Failed to save blog post: ${response.data.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error saving blog post:', err.response || err);
      setError(`Failed to save blog post: ${err.response?.data?.message || err.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle cancel button
  const handleCancel = () => {
    history.push('/adminblog/posts');
  };
  
  // Quill modules and formats
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link'],
      ['clean']
    ],
  };
  
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'color', 'background',
    'link'
  ];
  
  if (loading) {
    return (
      <div className="blog-admin-container">
        <h1>{isEditing ? 'Redigera inlägg' : 'Nytt inlägg'}</h1>
        <p>Laddar...</p>
      </div>
    );
  }
  
  return (
    <div className="blog-admin-container">
      <h1>{isEditing ? 'Redigera inlägg' : 'Nytt inlägg'}</h1>
      
      {error && <div className="error-message">{error}</div>}
      
      <form onSubmit={handleSubmit} className="blog-form">
        <div className="blog-form-group">
          <label htmlFor="title">Titel</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>
        
        <div className="blog-form-group">
          <label htmlFor="author">Författare</label>
          <input
            type="text"
            id="author"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            required
          />
        </div>
        
        <div className="blog-form-group">
          <label htmlFor="content">Innehåll</label>
          <ReactQuill
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            placeholder="Skriv innehållet här..."
            style={{ width: '100%' }}
          />
        </div>
        
        <div className="blog-form-group">
          <label htmlFor="image">Bild</label>
          <input
            type="file"
            id="image"
            accept="image/*"
            onChange={handleImageChange}
          />
          
          {(imagePreview || currentImage) && (
            <div>
              <p>Förhandsvisning:</p>
              <img
                src={imagePreview}
                alt="Preview"
                className="blog-preview-image"
              />
            </div>
          )}
        </div>
        
        <div className="blog-form-group">
          <div className="checkbox-group">
            <input
              type="checkbox"
              id="is_published"
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
            />
            <label htmlFor="is_published">Publicera inlägget</label>
          </div>
        </div>
        
        <div className="blog-form-buttons">
          <button
            type="button"
            className="cancel-button"
            onClick={handleCancel}
            disabled={saving}
          >
            Avbryt
          </button>
          
          <button
            type="submit"
            className="submit-button"
            disabled={saving}
          >
            {saving ? 'Sparar...' : (isEditing ? 'Uppdatera' : 'Publicera')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogPostForm; 